import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { Form, Input } from 'semantic-ui-react';
import {
  firstError,
  firstResponseError,
  pruneServerErrors,
  validationClasses,
} from '@/utils/errors';
import { states } from '@/constants/lifecycle';
import fields, { globalFieldLengths } from '@/constants/fields';
import FormValidationError from '@/components/errors/FormValidationError';
import Dialog from '@/components/dialogs/Dialog';
import VisibilityDropdown from '@/components/VisibilityDropdown';
import MarkdownEditor from '@/components/text-editors/MarkdownEditor';
import { hideSaveCohort, createCohort, updateActiveCohortProperties } from '../actions';
import { validateCohort } from '../helper';

const SaveCohortDialog = ({
  code,
  datasetCohortVisibilities,
  description,
  hide,
  name,
  postData,
  saveErrors,
  saveStatus,
  theme,
  updateProperties,
  visibility,
}) => {
  const intl = useIntl();
  const setErrors = (newErrors) => (updateProperties({ saveErrors: newErrors }));

  const setStatus = (status) => (updateProperties({ saveStatus: status }));

  const checkForErrors = () => {
    const validationErrors = validateCohort({
      code, name, description, isNew: true,
    });
    setErrors(validationErrors);

    return validationErrors;
  };

  const submitForm = () => {
    setStatus(states.PENDING);
    const validationErrors = checkForErrors();

    if (validationErrors.length > 0) {
      setStatus(states.NONE);
      return;
    }

    postData();
  };

  const codeError = (saveErrors || []).find((e) => (e.fields.includes(fields.CODE)));
  const nameError = (saveErrors || []).find((e) => (e.fields.includes(fields.NAME)));
  const descriptionError = (saveErrors || []).find((e) => (e.fields.includes(fields.DESCRIPTION)));

  return (
    <Dialog
      className="aridhia-save-cohort-dialog"
      error={firstResponseError(saveErrors)}
      icon="filter"
      inProgress={saveStatus === states.PENDING}
      minimumDataEntryMet={name}
      onClose={hide}
      onDismiss={() => setErrors(pruneServerErrors(saveErrors))}
      onSave={submitForm}
      saveMessageId="save.cohort.create.button"
      theme={theme}
      title={intl.formatMessage({ id: 'save.cohort.create.title' })}
    >
      <Form autoComplete="off" onSubmit={submitForm} error={firstError(saveErrors) !== null}>
        <FormValidationError error={codeError} htmlFor="cohort_code" labelId="save.cohort.code">
          <Input
            id="cohort_code"
            size="large"
            disabled={saveStatus === states.PENDING}
            type="custom"
            placeholder={intl.formatMessage({ id: 'save.cohort.code.placeholder' })}
            value={code || ''}
            maxLength={globalFieldLengths.code}
            onChange={(e) => updateProperties({ code: e.target.value })}
            className={validationClasses(codeError)}
          />
        </FormValidationError>
        <FormValidationError error={nameError} htmlFor="cohort_name" labelId="save.cohort.name">
          <Input
            id="cohort_name"
            autoFocus
            disabled={saveStatus === states.PENDING}
            type="custom"
            size="large"
            placeholder={intl.formatMessage({ id: 'save.cohort.name.placeholder' })}
            value={name}
            maxLength={globalFieldLengths.name}
            onChange={(e) => updateProperties({ name: e.target.value })}
            className={validationClasses(nameError)}
          />
        </FormValidationError>
        <FormValidationError htmlFor="cohort_visibility" labelId="save.cohort.visibility">
          <VisibilityDropdown
            id="cohort_visibility"
            disabled={saveStatus === states.PENDING}
            onChange={updateProperties}
            visibility={visibility}
            visibilityOptions={datasetCohortVisibilities}
          />
        </FormValidationError>
        <FormValidationError error={descriptionError} htmlFor="cohort_description" labelId="save.cohort.description">
          <MarkdownEditor
            id="cohort_description"
            disabled={saveStatus === states.PENDING}
            value={description}
            onChange={(value) => updateProperties({ description: value })}
            maxEditorHeight={80}
            minPreviewHeight={94}
            className={validationClasses(descriptionError)}
          />
        </FormValidationError>
      </Form>
    </Dialog>
  );
};

const mapStateToProps = ({
  cohorts: {
    activeCohort: {
      code,
      name,
      visibility,
      saveErrors,
      saveStatus,
      description,
      datasetCohortVisibilities,
    },
  },
}) => ({
  code,
  name,
  visibility,
  saveErrors,
  saveStatus,
  description,
  datasetCohortVisibilities,
});

const mapDispatchToProps = {
  hide: hideSaveCohort,
  updateProperties: updateActiveCohortProperties,
  postData: createCohort,
};

export default connect(mapStateToProps, mapDispatchToProps)(SaveCohortDialog);
